/**
 * The report body, shared by every period.
 *
 * Daily, Weekly, Monthly and Yearly are the same report over different
 * windows, so they are one component behind six routes rather than six pages
 * that drift apart. The period changes the default range, the bucket size and
 * the wording of the comparison — nothing else.
 *
 * The organising idea is that **every figure is a question**. Each tile, each
 * breakdown row and each sentence of the summary is a button that opens the
 * transactions behind it, because a number an owner cannot interrogate is a
 * number they have to take on trust.
 */

import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowDownRight,
  ArrowUpRight,
  Download,
  Info,
  Minus,
  MousePointerClick,
} from 'lucide-react';
import {
  REPORT_PERIOD_LABELS,
  type BreakdownRow,
  type CountGrowth,
  type MoneyGrowth,
  type ReportDimension,
  type ReportPeriod,
  type ReportSummary,
} from '@cenox/shared';
import { api, downloadFile, qs } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { count, money, moneyCompact, percent } from '../../lib/format';
import { Button, Card, CardHeader, ErrorState, Input, Spinner, cn } from '../../components/ui';
import { CurrencyNotice } from '../../components/CurrencyNotice';
import { LocationScope } from '../../components/LocationScope';
import { useLocationFilter, useLocationReady } from '../../lib/locationContext';
import { PageHeader } from '../../layouts/AppShell';
import { DrilldownModal, type DrilldownTarget } from './DrilldownModal';
import { NarrativeCard } from './NarrativeCard';
import { PALETTE, SERIES_COLOURS, paletteAt } from './palette';

interface Props {
  period: ReportPeriod;
}

const PERIOD_BLURB: Record<ReportPeriod, string> = {
  today: "Today's trading, hour by hour",
  daily: 'Day-by-day performance',
  weekly: 'Week-by-week performance',
  monthly: 'Month-by-month performance',
  yearly: 'Year-by-year performance',
  custom: 'Any range you choose',
};

export function ReportView({ period }: Props): ReactNode {
  const { business, can } = useAuth();
  const path = useBusinessPath();

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [drilldown, setDrilldown] = useState<DrilldownTarget | null>(null);

  /**
   * Empty means "whatever the server decides" — the business's own currency
   * when the window holds it, else the one carrying the most value. It is only
   * ever set by the reader clicking one of the currencies the report says are
   * present, so a single-currency business never sends it.
   */
  const [currencyCode, setCurrencyCode] = useState('');

  // The sidebar's Country → State/Region → Location, as on every screen.
  const scope = useLocationFilter();
  const locationReady = useLocationReady();

  const query = qs({
    period,
    from: from || undefined,
    to: to || undefined,
    currencyCode: currencyCode || undefined,
    ...scope,
  });

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['reports', business?.businessId, query],
    queryFn: () => api.get<ReportSummary>(path(`/reports/summary${query}`)),
    enabled: Boolean(business) && locationReady,
  });

  /**
   * The currency the REPORT states, not the business's default.
   *
   * These are the same thing for every single-currency business, and they
   * differ exactly when it matters: a window holding only a foreign branch's
   * takings, or a business that changed its default after trading. Labelling
   * those figures with the business's currency would put a naira sign on cedi
   * amounts, which is the failure the server-side guard exists to prevent.
   */
  const currency = data?.currency.code ?? business?.currencyCode ?? 'USD';
  // Loan lines are zero for a business with no loans; only then is net profit operating profit.
  const hasLoanLines = Boolean(
    data && [data.totals.financeIncome, data.totals.financeExpense, data.totals.badDebts].some((v) => v && Number(v) !== 0),
  );

  const exportUrl = (format: 'xlsx' | 'csv', dataset: string): string =>
    `/api/v1${path(
      `/exports${qs({
        dataset,
        format,
        period,
        from: from || undefined,
        to: to || undefined,
        // The file states the currency the reader is looking at, not the
        // default they might not have noticed they were switched away from.
        currencyCode: data?.currency.code,
        // The same scope as the figures on screen, so the file matches them.
        ...scope,
      })}`,
    )}`;

  return (
    <>
      <PageHeader
        title={`${REPORT_PERIOD_LABELS[period]} report`}
        description={PERIOD_BLURB[period]}
        action={
          can('reports:export') && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                icon={<Download className="size-4" />}
                onClick={() => void downloadFile(exportUrl('xlsx', 'report_breakdowns'), 'report.xlsx')}
              >
                Excel
              </Button>
              <Button
                variant="secondary"
                icon={<Download className="size-4" />}
                onClick={() => void downloadFile(exportUrl('csv', 'report_summary'), 'report.csv')}
              >
                CSV
              </Button>
            </div>
          )
        }
      />

      {/* Filters sit in one row above the charts. A custom range leads with its
          date inputs; the fixed periods keep them as an optional override. */}
      <Card className="mb-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Input
            label={period === 'custom' ? 'From' : 'From (optional)'}
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
          <Input
            label={period === 'custom' ? 'To' : 'To (optional)'}
            type="date"
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
          <div className="flex items-end">
            {(from || to) && (
              <Button
                variant="ghost"
                onClick={() => {
                  setFrom('');
                  setTo('');
                }}
              >
                Reset to default range
              </Button>
            )}
          </div>
        </div>
      </Card>

      {isPending ? (
        <Spinner label="Building your report" />
      ) : error ? (
        <ErrorState message="Could not build the report." onRetry={() => refetch()} />
      ) : (
        <div className="space-y-4">
          <LocationScope testId="report-scope" />
          <CurrencyNotice
            currency={data.currency}
            selected={Boolean(currencyCode)}
            onSelect={setCurrencyCode}
          />

          <NarrativeCard narrative={data.narrative} onDrill={setDrilldown} />

          <p className="flex items-center gap-1.5 text-xs text-slate-500">
            <MousePointerClick className="size-3.5" aria-hidden />
            Every figure below opens the transactions behind it.
          </p>

          {/* ── Headline figures ─────────────────────────────────────────── */}

          {Number(data.totals.refunds) > 0 && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Refunds given in this period: {money(data.totals.refunds, currency)}. Revenue below is sales made;
              net revenue is {money(data.totals.netRevenue, currency)}, and every profit figure is net of refunds.
            </p>
          )}

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <MetricTile
              label="Revenue"
              value={money(data.totals.revenue, currency)}
              growth={data.comparison.revenue}
              comparisonLabel={data.comparison.label}
              currency={currency}
              tone="income"
              onClick={() =>
                setDrilldown({
                  label: 'Revenue',
                  kind: 'sales',
                  expectedAmount: data.totals.revenue,
                })
              }
            />
            <MetricTile
              label="Money received"
              value={money(data.totals.collected, currency)}
              growth={data.comparison.collected}
              comparisonLabel={data.comparison.label}
              currency={currency}
              tone="neutral"
              hint="Payments banked in this period, whenever the sale happened"
              onClick={() =>
                setDrilldown({
                  label: 'Money received',
                  kind: 'payments',
                  expectedAmount: data.totals.collected,
                })
              }
            />
            <MetricTile
              label="Expenses"
              value={money(data.totals.expenses, currency)}
              growth={data.comparison.expenses}
              comparisonLabel={data.comparison.label}
              currency={currency}
              tone="outflow"
              // Rising costs are the bad direction, so the arrow's colour is
              // inverted relative to revenue.
              invertTone
              onClick={() =>
                setDrilldown({
                  label: 'Expenses',
                  kind: 'expenses',
                  expectedAmount: data.totals.expenses,
                })
              }
            />
            <MetricTile
              label="Net profit"
              value={money(data.totals.netProfit, currency)}
              growth={data.comparison.netProfit}
              comparisonLabel={data.comparison.label}
              currency={currency}
              tone={data.totals.netProfit.startsWith('-') ? 'outflow' : 'income'}
              hint={hasLoanLines ? 'Operating profit, plus loan income, less loan costs' : 'Revenue minus expenses'}
              onClick={() =>
                setDrilldown({
                  // The drill-down lists sales and expenses, so it explains
                  // operating profit; the loan lines are shown beside it.
                  label: hasLoanLines ? 'Operating profit' : 'Net profit',
                  kind: 'profit',
                  expectedAmount: data.totals.operatingProfit,
                })
              }
            />
          </div>

          {hasLoanLines && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm" aria-label="Below operating profit">
              <p className="font-medium text-slate-900">Below operating profit</p>
              <p className="text-xs text-slate-500">From the loan ledger. Loan principal is never revenue or an expense.</p>
              <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div><dt className="text-xs text-slate-500">Operating profit</dt><dd className="tabular-nums">{money(data.totals.operatingProfit, currency)}</dd></div>
                <div><dt className="text-xs text-slate-500">Finance income</dt><dd className="tabular-nums text-emerald-700">+{money(data.totals.financeIncome, currency)}</dd></div>
                <div><dt className="text-xs text-slate-500">Finance costs</dt><dd className="tabular-nums">−{money(data.totals.financeExpense, currency)}</dd></div>
                <div><dt className="text-xs text-slate-500">Bad debts</dt><dd className="tabular-nums">−{money(data.totals.badDebts, currency)}</dd></div>
              </dl>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <MetricTile
              label="Outstanding"
              value={money(data.totals.outstanding, currency)}
              currency={currency}
              tone="owed"
              hint="Still unpaid from this period"
              onClick={() =>
                setDrilldown({
                  label: 'Outstanding',
                  kind: 'outstanding',
                  expectedAmount: data.totals.outstanding,
                })
              }
            />
            <MetricTile
              label="Sales"
              value={count(data.totals.transactionCount)}
              growth={data.comparison.transactionCount}
              comparisonLabel={data.comparison.label}
              currency={currency}
              tone="neutral"
              onClick={() =>
                setDrilldown({
                  label: 'All sales',
                  kind: 'sales',
                  expectedAmount: data.totals.revenue,
                })
              }
            />
            <MetricTile
              label="Average sale"
              value={money(data.totals.averageTransaction, currency)}
              growth={data.comparison.averageTransaction}
              comparisonLabel={data.comparison.label}
              currency={currency}
              tone="neutral"
              onClick={() =>
                setDrilldown({
                  label: 'Average sale',
                  kind: 'sales',
                  expectedAmount: data.totals.revenue,
                  note: `The average is ${money(data.totals.revenue, currency)} across ${count(
                    data.totals.transactionCount,
                  )} sales. Those sales are listed below.`,
                })
              }
            />
            <MetricTile
              label="Gross profit"
              value={
                data.totals.grossProfit === null
                  ? 'Not available'
                  : money(data.totals.grossProfit, currency)
              }
              currency={currency}
              tone="neutral"
              muted={data.totals.grossProfit === null}
              hint={
                data.totals.grossProfit === null
                  ? 'No cost price recorded on any sale in this period'
                  : `Covers the ${percent(data.totals.grossProfitCoverage)} of revenue with cost data`
              }
              // Nothing to open when no sale in range carries a cost price.
              onClick={
                data.totals.grossProfit === null
                  ? undefined
                  : () =>
                      setDrilldown({
                        label: 'Gross profit',
                        kind: 'gross_profit',
                        expectedAmount: data.totals.grossProfit ?? undefined,
                      })
              }
            />
          </div>

          {/* ── Customers ────────────────────────────────────────────────── */}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <MetricTile
              label="Customers"
              value={count(data.totals.customerCount)}
              growth={data.comparison.customerCount}
              comparisonLabel={data.comparison.label}
              currency={currency}
              tone="neutral"
              onClick={() =>
                setDrilldown({
                  label: 'Customers',
                  kind: 'sales',
                  customers: 'all',
                  expectedCount: data.totals.customerCount,
                  note: 'Every sale in this period that names a customer. Walk-in sales are excluded, which is why this can list fewer sales than the Revenue tile.',
                })
              }
            />
            <MetricTile
              label="New customers"
              value={count(data.totals.newCustomerCount)}
              currency={currency}
              tone="income"
              hint="First ever purchase fell in this period"
              onClick={() =>
                setDrilldown({
                  label: 'New customers',
                  kind: 'sales',
                  customers: 'new',
                  expectedCount: data.totals.newCustomerCount,
                  note: 'Sales from customers whose first ever purchase falls inside this period.',
                })
              }
            />
            <MetricTile
              label="Returning customers"
              value={count(data.totals.returningCustomerCount)}
              currency={currency}
              tone="neutral"
              hint="Had bought before this period"
              onClick={() =>
                setDrilldown({
                  label: 'Returning customers',
                  kind: 'sales',
                  customers: 'returning',
                  expectedCount: data.totals.returningCustomerCount,
                  note: 'Sales from customers who had already bought before this period began.',
                })
              }
            />
          </div>

          {data.totals.grossProfit !== null &&
            (data.totals.grossProfitCoverage ?? 100) < 100 && (
              <div className="flex gap-2.5 rounded-lg border border-brand-200 bg-brand-50 px-3.5 py-2.5">
                <Info className="mt-0.5 size-4 shrink-0 text-brand-600" aria-hidden />
                <p className="text-sm text-brand-900">
                  Gross profit covers only the{' '}
                  <strong>{percent(data.totals.grossProfitCoverage)}</strong> of revenue where a
                  cost price was recorded. Add cost prices to your sales for a complete margin.
                </p>
              </div>
            )}

          {/* ── The shape of the period ──────────────────────────────────── */}

          <Card>
            <CardHeader
              title="Revenue, expenses and profit"
              subtitle={`${data.range.from} to ${data.range.to} · compared with ${data.comparison.range.from} to ${data.comparison.range.to}`}
            />
            <SeriesChart data={data} currency={currency} />
          </Card>

          {/* ── Breakdowns ───────────────────────────────────────────────── */}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <BreakdownCard
              title="Revenue by channel"
              subtitle="Where the money came from"
              rows={data.byChannel}
              currency={currency}
              onDrill={setDrilldown}
            />
            <BreakdownCard
              title="Expenses by category"
              subtitle="Where the money went"
              rows={data.byExpenseCategory}
              currency={currency}
              kind="expenses"
              onDrill={setDrilldown}
            />
            <BreakdownCard
              title="Revenue by payment method"
              subtitle="How customers paid"
              rows={data.byPaymentMethod}
              currency={currency}
              onDrill={setDrilldown}
            />
            <BreakdownCard
              title="Payment status"
              subtitle="What has been settled"
              rows={data.byPaymentStatus}
              currency={currency}
              onDrill={setDrilldown}
            />
            <BreakdownCard
              title="Top customers"
              subtitle="By revenue in this period"
              rows={data.byCustomer}
              currency={currency}
              onDrill={setDrilldown}
            />
            {worthCharting(data.byProduct) && (
              <BreakdownCard
                title="Products and services"
                subtitle="Best sellers in this period"
                rows={data.byProduct}
                currency={currency}
                onDrill={setDrilldown}
              />
            )}
            {worthCharting(data.byBranch) && (
              <BreakdownCard
                title="Branch performance"
                subtitle="Revenue by location"
                rows={data.byBranch}
                currency={currency}
                onDrill={setDrilldown}
              />
            )}
            {worthCharting(data.byStaff) && (
              <BreakdownCard
                title="Staff performance"
                subtitle="Revenue credited to each team member"
                rows={data.byStaff}
                currency={currency}
                onDrill={setDrilldown}
              />
            )}
          </div>
        </div>
      )}

      <DrilldownModal
        target={drilldown}
        period={period}
        from={from || undefined}
        to={to || undefined}
        currencyCode={data?.currency.code}
        scope={scope}
        onClose={() => setDrilldown(null)}
      />
    </>
  );
}

// ── Metric tile ─────────────────────────────────────────────────────────────

function MetricTile({
  label,
  value,
  growth,
  comparisonLabel,
  currency,
  tone,
  hint,
  muted,
  invertTone,
  onClick,
}: {
  label: string;
  value: string;
  growth?: MoneyGrowth | CountGrowth;
  comparisonLabel?: string;
  currency: string;
  tone: 'income' | 'outflow' | 'owed' | 'neutral';
  hint?: string;
  muted?: boolean;
  invertTone?: boolean;
  onClick?: () => void;
}): ReactNode {
  const colours = {
    income: 'text-emerald-700',
    outflow: 'text-red-700',
    owed: 'text-amber-700',
    neutral: 'text-slate-900',
  };

  const body = (
    <>
      <p className="text-xs font-medium text-slate-600">{label}</p>
      <p
        className={cn(
          'mt-1.5 text-lg font-semibold tabular sm:text-xl',
          muted ? 'text-slate-400' : colours[tone],
        )}
      >
        {value}
      </p>
      {growth && (
        <GrowthIndicator
          growth={growth}
          currency={currency}
          comparisonLabel={comparisonLabel}
          invert={invertTone}
        />
      )}
      {hint && <p className="mt-1 text-xs leading-snug text-slate-500">{hint}</p>}
    </>
  );

  if (!onClick) return <div className="card p-4">{body}</div>;

  return (
    <button
      type="button"
      onClick={onClick}
      className="card p-4 text-left transition-colors hover:border-brand-300 hover:bg-brand-50/40"
      title="Show the transactions behind this figure"
    >
      {body}
    </button>
  );
}

/**
 * Growth against the previous equivalent period.
 *
 * A null percentage means the previous period was zero. That is rendered as
 * "new" rather than as a very large number, because "up 4,300%" from a base of
 * almost nothing is technically true and practically meaningless.
 */
function GrowthIndicator({
  growth,
  currency,
  comparisonLabel,
  invert,
}: {
  growth: MoneyGrowth | CountGrowth;
  currency: string;
  comparisonLabel?: string;
  invert?: boolean;
}): ReactNode {
  const isMoney = typeof growth.current === 'string';

  if (growth.changePercent === null) {
    const appeared = isMoney ? growth.current !== '0.00' : Number(growth.current) > 0;
    return (
      <p className="mt-1 text-xs text-slate-500">
        {appeared ? 'New this period' : `No change ${comparisonLabel ?? ''}`.trim()}
      </p>
    );
  }

  const good = invert ? growth.direction === 'down' : growth.direction === 'up';
  const Icon =
    growth.direction === 'up' ? ArrowUpRight : growth.direction === 'down' ? ArrowDownRight : Minus;

  return (
    <p
      className={cn(
        'mt-1 flex items-center gap-1 text-xs font-medium',
        growth.direction === 'flat' ? 'text-slate-500' : good ? 'text-emerald-700' : 'text-red-700',
      )}
    >
      <Icon className="size-3.5 shrink-0" aria-hidden />
      <span className="tabular">
        {growth.changePercent > 0 ? '+' : ''}
        {growth.changePercent.toFixed(1)}%
      </span>
      <span className="font-normal text-slate-500">
        {isMoney
          ? `from ${moneyCompact(growth.previous as string, currency)}`
          : `from ${count(growth.previous as number)}`}
      </span>
    </p>
  );
}

// ── Series chart ────────────────────────────────────────────────────────────

/**
 * Revenue and expenses as bars, net profit as a line — all three on **one**
 * axis, because all three are the same currency. A second axis would let the
 * eye compare two different scales as though they were one, which is the
 * quickest way to make a chart lie.
 */
function SeriesChart({ data, currency }: { data: ReportSummary; currency: string }): ReactNode {
  const series = data.series.map((point) => ({
    label: point.label,
    Revenue: Number(point.revenue),
    Expenses: Number(point.expenses),
    'Net profit': Number(point.operatingProfit),
  }));

  if (series.every((p) => p.Revenue === 0 && p.Expenses === 0)) {
    return (
      <p className="py-12 text-center text-sm text-slate-500">
        No activity recorded in this period.
      </p>
    );
  }

  return (
    <div className="h-72 sm:h-96">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={series} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: '#64748b' }}
            tickLine={false}
            axisLine={{ stroke: '#e2e8f0' }}
            interval="preserveStartEnd"
            minTickGap={16}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#64748b' }}
            tickLine={false}
            axisLine={false}
            width={68}
            tickFormatter={(value: number) =>
              new Intl.NumberFormat('en-US', { notation: 'compact' }).format(value)
            }
          />
          <Tooltip
            formatter={(value: unknown, name: unknown) => [
              money(Number(value).toFixed(2), currency),
              String(name ?? ''),
            ]}
            cursor={{ fill: '#f1f5f9' }}
            contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {/* 2px gap between adjacent bars keeps the pair legible at density. */}
          <Bar dataKey="Revenue" fill={SERIES_COLOURS.revenue} radius={[4, 4, 0, 0]} barSize={14} />
          <Bar dataKey="Expenses" fill={SERIES_COLOURS.expenses} radius={[4, 4, 0, 0]} barSize={14} />
          <Line
            type="monotone"
            dataKey="Net profit"
            stroke={SERIES_COLOURS.profit}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Breakdown ───────────────────────────────────────────────────────────────

/** What is missing from a transaction that lands it in the unattributed row. */
const UNATTRIBUTED_MEANING: Record<ReportDimension, string> = {
  channel: 'sales channel',
  branch: 'branch',
  product: 'product or service',
  staff: 'staff member',
  customer: 'customer (walk-in trade)',
  paymentMethod: 'payment method',
  paymentStatus: 'payment status',
  expenseCategory: 'expense category',
};

function BreakdownCard({
  title,
  subtitle,
  rows,
  currency,
  kind = 'sales',
  onDrill,
}: {
  title: string;
  subtitle: string;
  rows: BreakdownRow[];
  currency: string;
  kind?: 'sales' | 'expenses';
  onDrill: (target: DrilldownTarget) => void;
}): ReactNode {
  if (rows.length === 0) {
    return (
      <Card>
        <CardHeader title={title} subtitle={subtitle} />
        <p className="py-8 text-center text-sm text-slate-500">Nothing to show for this period.</p>
      </Card>
    );
  }

  const visible = rows.slice(0, 8);
  const unattributed = rows.find((row) => row.key === 'unattributed');

  return (
    <Card>
      <CardHeader title={title} subtitle={subtitle} />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        {/* A donut is only honest with a handful of slices; beyond that the
            bars below carry the comparison on their own. */}
        {visible.length <= 6 && (
          <div className="h-40 w-full sm:w-40 sm:shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={visible.map((row) => ({ name: row.label, value: Number(row.amount) }))}
                  dataKey="value"
                  innerRadius="55%"
                  outerRadius="85%"
                  paddingAngle={2}
                  stroke="#ffffff"
                  strokeWidth={2}
                >
                  {visible.map((_, index) => (
                    <Cell key={index} fill={paletteAt(index)} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: unknown) => money(Number(value).toFixed(2), currency)}
                  contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        <ul className="min-w-0 flex-1 space-y-2">
          {visible.map((row, index) => (
            <li key={row.key}>
              <button
                type="button"
                onClick={() =>
                  onDrill({
                    label: row.label,
                    kind,
                    dimension: (row.dimension ?? undefined) as ReportDimension | undefined,
                    key: row.key,
                    expectedAmount: row.amount,
                  })
                }
                className="w-full rounded-lg px-2 py-1.5 -mx-2 text-left transition-colors hover:bg-slate-50"
                title={`Show the ${row.label} transactions`}
              >
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      className="size-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: paletteAt(index) }}
                      aria-hidden
                    />
                    {/* The written label is the primary encoding; the swatch
                        only reinforces it, so the row still reads in greyscale. */}
                    <span className="truncate text-slate-700">{row.label}</span>
                  </span>
                  <span className="shrink-0 font-medium tabular text-slate-900">
                    {money(row.amount, currency)}
                  </span>
                </div>

                <div className="mt-1 flex items-center gap-2">
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${row.percentage ?? 0}%`,
                        backgroundColor: paletteAt(index),
                      }}
                    />
                  </div>
                  <span className="w-11 shrink-0 text-right text-xs tabular text-slate-500">
                    {percent(row.percentage)}
                  </span>
                  <span className="w-16 shrink-0 text-right text-xs tabular">
                    {row.changePercent === null ? (
                      <span className="text-slate-400">—</span>
                    ) : (
                      <span
                        className={cn(
                          row.changePercent > 0
                            ? 'text-emerald-700'
                            : row.changePercent < 0
                              ? 'text-red-700'
                              : 'text-slate-500',
                        )}
                      >
                        {row.changePercent > 0 ? '+' : ''}
                        {row.changePercent.toFixed(1)}%
                      </span>
                    )}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/*
        An "Unassigned" slice is the single most confusing thing on a report:
        it looks like missing data, and the reader cannot tell whether it is a
        bug or simply how the sales were recorded. Naming what it means, and
        pointing out that it opens like any other row, turns it from a worry
        into a to-do list.
      */}
      {unattributed && (
        <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-600">
          <strong className="font-medium text-slate-700">{unattributed.label}</strong> is{' '}
          {money(unattributed.amount, currency)} across {count(unattributed.count)}{' '}
          {unattributed.count === 1 ? 'transaction' : 'transactions'} with no{' '}
          {UNATTRIBUTED_MEANING[unattributed.dimension ?? 'channel']} recorded. Click it to see
          exactly which ones.
        </p>
      )}

      {rows.length > visible.length && (
        <p className="mt-3 text-xs text-slate-500">
          Showing the top {visible.length} of {rows.length}.
        </p>
      )}
    </Card>
  );
}

export { PALETTE };


/**
 * Is this breakdown worth drawing?
 *
 * A single row means "100% of it went to the only thing there is" — true, and
 * noise. Nothing but the unattributed bucket means the business never records
 * that dimension, and a chart reading 100% Unassigned teaches nobody anything.
 *
 * This used to live in the API, which made its figures non-additive: a report
 * for one location has one branch row, so it was suppressed, and the
 * per-location totals stopped summing to the whole business. The API now returns
 * the truth and this decides what to render — which is the right side of the
 * line for a question about charts.
 */
export function worthCharting(rows: Array<{ key: string }>): boolean {
  if (rows.length <= 1) return false;
  return rows.some((row) => row.key !== 'unattributed');
}
